import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { messagesAPI, notificationsAPI } from "@/lib/api";
import {
  Store,
  MessagesSquare,
  Users,
  Search,
  Home,
  Mail,
  Menu,
  X,
  LogOut,
  User as UserIcon,
  Settings as SettingsIcon,
  Trophy,
  Gauge,
  Activity,
  Tag,
  ShieldAlert,
  ChevronDown,
  Award,
  Bell,
  Receipt,
  PackageSearch,
  Sun,
  Moon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import FooterLinks from "@/components/FooterLinks";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ContactModal from "@/components/ContactModal";

// Top-level web route prefixes that actually exist in the app router.
const KNOWN_WEB_PREFIXES = [
  "marketplace", "services", "storefront", "forum", "social", "messages",
  "profile", "orders", "order", "watchlist", "settings", "points", "admin",
  "sponsorship", "price-compare", "clubs", "simhub", "u", "learn", "membership",
  "notifications", "checkout", "transactions", "saved-listings",
];
const isKnownWebRoute = (url) => {
  if (!url || !url.startsWith("/")) return false;
  const seg = url.replace(/^\//, "").split(/[/?#]/)[0];
  return KNOWN_WEB_PREFIXES.includes(seg);
};

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/marketplace", label: "Marketplace", icon: Store },
  { to: "/price-compare", label: "Price Compare", icon: PackageSearch },
  { to: "/sponsorship", label: "Sponsorship", icon: Award },
  { to: "/social", label: "Social", icon: Users },
  { to: "/forum", label: "Forum", icon: MessagesSquare },
];

// Inline SVG icon matching Ionicons `podium-outline` so Leaderboards icon
// matches the mobile drawer visually.
const Podium = ({ className = "", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <rect x="9" y="6" width="6" height="15" rx="1" />
    <rect x="2" y="11" width="6" height="10" rx="1" />
    <rect x="16" y="9" width="6" height="12" rx="1" />
  </svg>
);

// Inline SVG matching the mobile Clubs icon (MaterialCommunityIcons
// `account-group`): one prominent figure in front-centre with two smaller
// figures behind on either side.
const UserGroup = ({ className = "", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    {...props}
  >
    {/* Back-left figure */}
    <circle cx="5" cy="8" r="2.1" opacity="0.6" />
    <path d="M1.5 16.5c0-1.9 1.57-3.4 3.5-3.4.5 0 .97.1 1.4.28-.76.86-1.2 1.9-1.2 3.12v.5H1.5v-.5z" opacity="0.6" />
    {/* Back-right figure */}
    <circle cx="19" cy="8" r="2.1" opacity="0.6" />
    <path d="M22.5 16.5c0-1.9-1.57-3.4-3.5-3.4-.5 0-.97.1-1.4.28.76.86 1.2 1.9 1.2 3.12v.5h3.7v-.5z" opacity="0.6" />
    {/* Front-centre figure */}
    <circle cx="12" cy="7" r="3" />
    <path d="M6 18.5c0-3.03 2.69-5.5 6-5.5s6 2.47 6 5.5v.5H6v-.5z" />
  </svg>
);

const MORE_NAV = [
  { to: "/my-listings", label: "My Listings", icon: Tag },
  { to: "/points", label: "Points Portal", icon: Trophy },
  { to: "/leaderboards", label: "Leaderboards", icon: Podium },
  { to: "/clubs", label: "Clubs", icon: UserGroup },
  { to: "/simhub", label: "SimHub Stats", icon: Activity },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [previewShown, setPreviewShown] = useState(false);
  const [notifDialog, setNotifDialog] = useState({ open: false, mode: "full", title: "", body: "" });
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    messagesAPI
      .getUnreadCount()
      .then((res) => setUnread(res.data?.unread_count || res.data?.count || 0))
      .catch(() => {});
    notificationsAPI
      .list({ limit: 20 })
      .then((res) => {
        const list = res.data?.notifications || [];
        const unreadCount = res.data?.unread_count || 0;
        setNotifs(list);
        setNotifUnread(unreadCount);

        // One-shot preview on login: show a single consolidated toast with a
        // visible close button so it never blocks the top-right indefinitely.
        const key = `notif-preview-shown:${user.id}`;
        try {
          if (unreadCount > 0 && !sessionStorage.getItem(key) && !previewShown) {
            const unread = list.filter((n) => !n.is_read).slice(0, 2);
            const titles = unread.map((n) => n.title || "New notification").join(" · ");
            const extra = unreadCount > unread.length
              ? ` (+ ${unreadCount - unread.length} more)`
              : "";
            toast(`${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`, {
              description: `${titles}${extra}` || undefined,
              duration: 7000,
              closeButton: true,
            });
            sessionStorage.setItem(key, "1");
            setPreviewShown(true);
          }
        } catch { /* ignore storage errors (private mode) */ }
      })
      .catch(() => {});
  }, [user, location.pathname]);

  const openNotification = async (n) => {
    try { if (!n.is_read) await notificationsAPI.markRead(n.id); } catch { /* ignore */ }
    let url = n.action_url || n.data?.action_url;
    if (url && url.startsWith("/points-portal")) {
      url = url.replace("/points-portal", "/points");
    }
    // Mobile-style conversation deep link -> web messages route
    if (url) {
      const conv = url.match(/^\/conversation\/([^/?#]+)/);
      if (conv) url = `/messages/${conv[1]}`;
    }
    if (url) {
      if (url.startsWith("/api/")) {
        window.open(url, "_blank");
      } else if (isKnownWebRoute(url)) {
        navigate(url);
      } else {
        // Unmatched link fallback (item 3)
        setNotifDialog({ open: true, mode: "unmatched", title: "", body: "" });
      }
    } else {
      // No link: show the full notification message (item 2)
      setNotifDialog({ open: true, mode: "full", title: n.title || "Notification", body: n.body || "" });
    }
    notificationsAPI.list({ limit: 20 }).then((r) => {
      setNotifs(r.data?.notifications || []);
      setNotifUnread(r.data?.unread_count || 0);
    }).catch(() => {});
  };

  const markAllRead = async () => {
    try { await notificationsAPI.markAllRead(); setNotifUnread(0); setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true }))); } catch { /* ignore */ }
  };

  const isActive = (item) =>
    item.end
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.username || "U").slice(0, 2).toUpperCase();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0a0d] chrome-dark">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <Logo size="sm" />
          </Link>

          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item)
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                  {item.to === "/messages" && unread > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                      {unread}
                    </span>
                  )}
                </Link>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
                  data-testid="nav-more"
                >
                  More <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 chrome-dark bg-popover">
                {MORE_NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.to} onClick={() => navigate(item.to)} data-testid={`nav-more-${item.to.slice(1)}`}>
                      <Icon className="mr-2 h-4 w-4" /> {item.label}
                    </DropdownMenuItem>
                  );
                })}
                {(user?.is_admin || user?.is_super_admin) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-more-admin">
                      <ShieldAlert className="mr-2 h-4 w-4 text-primary" /> Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleTheme} data-testid="nav-more-theme">
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />} {isDark ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <Button
              onClick={() => navigate("/marketplace/create")}
              className="hidden bg-primary text-primary-foreground hover:bg-primary/90 sm:inline-flex"
              data-testid="nav-sell-button"
            >
              Sell an item
            </Button>
            {user ? (
              <>
                <button
                  onClick={() => navigate("/messages")}
                  className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  data-testid="nav-messages-icon"
                  aria-label="Messages"
                >
                  <Mail className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                      {unread}
                    </span>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                      data-testid="nav-notifications"
                      aria-label="Notifications"
                    >
                      <Bell className="h-5 w-5" />
                      {notifUnread > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                          {notifUnread}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto chrome-dark bg-popover">
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <p className="text-sm font-semibold">Notifications</p>
                      {notifUnread > 0 && (
                        <button className="text-[11px] text-primary hover:underline" onClick={markAllRead} data-testid="notifs-mark-all-read">Mark all read</button>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    {notifs.length === 0 ? (
                      <p className="px-3 py-4 text-center text-xs text-muted-foreground">You&apos;re all caught up.</p>
                    ) : (
                      notifs.map((n) => (
                        <button
                          key={n.id}
                          onClick={() => openNotification(n)}
                          className={`flex w-full flex-col items-start gap-0.5 rounded-sm px-3 py-2 text-left text-xs hover:bg-secondary/60 ${n.is_read ? "opacity-70" : ""}`}
                          data-testid={`notif-${n.id}`}
                        >
                          <div className="flex w-full items-start gap-2">
                            {!n.is_read && <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />}
                            <p className="flex-1 font-semibold">{n.title}</p>
                          </div>
                          <p className="line-clamp-2 text-muted-foreground">{n.body}</p>
                          {(n.notification_type === "member_message" || n.data?.type === "member_message") && (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                              Read Message
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-full"
                    data-testid="nav-user-menu"
                  >
                    <Avatar className="h-9 w-9 border border-border">
                      <AvatarImage src={user.profile_image} />
                      <AvatarFallback className="bg-secondary text-sm">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 chrome-dark bg-popover">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user.username}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <UserIcon className="mr-2 h-4 w-4" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                    <Mail className="mr-2 h-4 w-4" /> Messages
                    {unread > 0 && (
                      <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{unread}</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/storefront/edit")} data-testid="menu-storefront">
                    <Store className="mr-2 h-4 w-4" /> My Storefront
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                    <Receipt className="mr-2 h-4 w-4" /> My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/watchlist")} data-testid="menu-watchlist">
                    <Bell className="mr-2 h-4 w-4" /> Watchlist
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} data-testid="menu-theme-toggle">
                    {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />} {isDark ? "Light mode" : "Dark mode"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <Button onClick={() => navigate("/login")} variant="outline" data-testid="nav-login">
                Sign in
              </Button>
            )}
            <button
              className="lg:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        <div className="racing-line" />

        {mobileOpen && (
          <nav className="border-t border-border bg-card px-4 py-2 lg:hidden">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium ${
                    isActive(item) ? "bg-secondary text-foreground" : "text-muted-foreground"
                  }`}
                >
                  <Icon className="h-5 w-5" /> {item.label}
                </Link>
              );
            })}
            {MORE_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-muted-foreground">
                  <Icon className="h-5 w-5" /> {item.label}
                </Link>
              );
            })}
            {(user?.is_admin || user?.is_super_admin) && (
              <Link to="/admin" className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-primary">
                <ShieldAlert className="h-5 w-5" /> Admin Dashboard
              </Link>
            )}
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6">{children}</main>

      <div className="racing-line" />
      <footer className="border-t border-white/10 bg-[#0a0a0d] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-6 sm:px-6">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_throttle-hub-6/artifacts/krvfdgqm_Powered%20by%20PLSR%20%28cropped%29.png"
            alt="Powered by PLSR"
            className="h-9 w-auto opacity-80"
            draggable={false}
            data-testid="footer-powered-by"
          />
          <div className="flex items-center gap-4 text-xs text-white/60">
            <FooterLinks tone="dark" />
          </div>
        </div>
      </footer>

      <Dialog open={notifDialog.open} onOpenChange={(o) => setNotifDialog((d) => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{notifDialog.mode === "unmatched" ? "Something went wrong" : (notifDialog.title || "Notification")}</DialogTitle>
          </DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-foreground/85">
            {notifDialog.mode === "unmatched"
              ? "Sorry, it looks like there is an issue opening that link. You can head to Contact to let our team know, or dismiss this and try again later."
              : notifDialog.body}
          </p>
          <DialogFooter>
            {notifDialog.mode === "unmatched" && (
              <Button variant="outline" onClick={() => { setNotifDialog((d) => ({ ...d, open: false })); setContactOpen(true); }} data-testid="notif-unmatched-contact">Contact the team</Button>
            )}
            <Button onClick={() => setNotifDialog((d) => ({ ...d, open: false }))} data-testid="notif-dialog-ok">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
